import {Component, inject, OnInit, DestroyRef, ViewChild} from '@angular/core';
import {Router, NavigationEnd, ActivatedRoute} from '@angular/router';
import { NavigationToggleService } from '../../services/navigation-toggle.service';
import { CommonModule } from '@angular/common';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faPencil, faX } from '@fortawesome/free-solid-svg-icons';
import { faUser } from '@fortawesome/free-regular-svg-icons';
import { DataService } from '../../../api/services/data.service';
import {filter, take} from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {MatDivider} from '@angular/material/divider';
import {MatIcon} from '@angular/material/icon';
import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {TableContextMenuService} from '../../services/table-context-menu.service';
import {ContextMenuAction} from '../base-material-table/base-material-table.component';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    FaIconComponent,
    MatDivider,
    MatIcon,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent implements OnInit {
  @ViewChild('companyMenuTrigger') companyMenuTrigger!: MatMenuTrigger;

  protected readonly faX = faX;
  protected readonly faPencil = faPencil;
  protected readonly faUser = faUser;

  private navigationToggleService = inject(NavigationToggleService);
  readonly open$ = this.navigationToggleService.open$;
  private dataService = inject(DataService);
  readonly data$ = this.dataService.currentData;
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  // Context Menu State
  contextMenuPosition = { x: 0, y: 0 };
  selectedRow: any | null = null;
  contextMenuActions: ContextMenuAction[] = [];
  cachedContextMenuActions: Array<ContextMenuAction & { isDisabled: boolean }> = [];

  constructor(
    private contextMenuService: TableContextMenuService,
    private route: ActivatedRoute
  ) {
  }

  ngOnInit() {
    this.initializeCompanyContextMenuActions();
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.onSidebarClose();
      });
  }

  /**
   * Initialisiert die Context Menu Actions für Company Links
   */
  private initializeCompanyContextMenuActions(): void {
    this.contextMenuActions = this.contextMenuService.getStandardActions({
      entityType: 'ansprechpartner',
      route: this.route,
      showDelete: false // Keine Delete-Action in Relations
    });
  }

  /**
   * Öffnet das Context Menu beim Rechtsklick auf Company Name
   */
  onContextMenu(event: MouseEvent, row: any): void {
    event.preventDefault();
    event.stopPropagation(); // Verhindert dass Row-Context-Menu öffnet

    this.selectedRow = row.contact;

    // Filtere sichtbare Actions und cache disabled state
    this.cachedContextMenuActions = this.contextMenuActions
      .filter(action => !action.visible || action.visible(row))
      .map(action => ({
        ...action,
        isDisabled: action.disabled ? action.disabled(row) : false
      }));

    // Setze Menu Position
    this.contextMenuPosition.x = event.clientX;
    this.contextMenuPosition.y = event.clientY;

    // Öffne Menu
    this.companyMenuTrigger.openMenu();
  }

  /**
   * Führt eine Context Menu Action aus
   */
  executeContextAction(action: ContextMenuAction & { isDisabled: boolean }, event: MouseEvent): void {
    if (this.selectedRow) {
      action.action(this.selectedRow, event);
    }
  }

  /**
   * Gibt sichtbare Context Menu Actions zurück
   */
  getVisibleContextActions(): Array<ContextMenuAction & { isDisabled: boolean }> {
    return this.cachedContextMenuActions;
  }

  /**
   * Prüft ob Icon FontAwesome ist
   */
  getIconFontSet(icon: string): string {
    const fontAwesomeIcons = [
      'external-link', 'window-restore', 'copy', 'trash',
      'columns', 'pencil', 'unlink'
    ];
    return fontAwesomeIcons.includes(icon) || icon.startsWith('fa-') ? 'fa' : '';
  }

  onNavigate(nav: string) {
    // Hole die aktuellen Daten einmalig
    this.data$.pipe(take(1)).subscribe(data => {
      const navigationExtras: any = {};

      // Wenn return_url vorhanden ist, als QueryParam mitgeben
      if (data?.return_url) {
        navigationExtras.queryParams = { return_url: data.return_url };
      }

      setTimeout(() => {
        this.router.navigate([nav], navigationExtras).then(() => this.onSidebarClose());
      });
    });
  }

  onSidebarToggle() {
    this.navigationToggleService.toggle();
  }

  onSidebarOpen() {
    this.navigationToggleService.open();
  }

  onSidebarClose() {
    this.navigationToggleService.close();
  }
}
